import { FirmDataTransaction } from '@/api/custom';

/**
 * Map firm-data transactions onto the deposit/withdrawal shape the wealth
 * page's Recent Transfers table already renders.
 *
 * The table splits rows by direction and tells them apart by the presence of
 * `deposit_id` / `withdrawal_id`, so firm-data rows are mapped onto those
 * same keys instead of teaching the UI a second shape.
 *
 * The bank fields are deliberately absent: firm-data records the movement of
 * money, not which bank relationship funded it.
 *
 * A transfer whose status nothing has reported yet carries no `state` at all,
 * rather than being defaulted to pending — the Status column renders a dash
 * for it, which is honest about not knowing.
 */

export type TransferRow = {
  account_id: string;
  amount: string;
  created_at: string;
  deposit_id?: string;
  withdrawal_id?: string;
  state?: string;
};

// firm-data's custodian-agnostic statuses, labelled for display. Anything
// outside this list is shown as-is rather than hidden, so a status added
// later still reaches the investor instead of silently vanishing.
const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  completed: 'Completed',
  failed: 'Failed',
  canceled: 'Canceled',
};

const transferState = (status?: string | null): string | undefined => {
  if (!status) return undefined;
  const normalized = status.trim().toLowerCase();
  if (!normalized) return undefined;
  return STATUS_LABELS[normalized] ?? status;
};

export type TransferList = {
  deposits: TransferRow[];
  withdrawals: TransferRow[];
};

const DEPOSIT_TYPE = 'deposit';
const WITHDRAWAL_TYPE = 'withdrawal';

// firm-data's own id for the row is the fallback: every transfer mirrored from
// a custodian carries that custodian's transfer id, but a transaction from
// another source (a custodian file import, say) may not.
const transferId = (transaction: FirmDataTransaction): string =>
  transaction.external_transfer_id || transaction.transaction_id;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A transfer's date as the investor should read it.
 *
 * firm-data records `trade_date` as a calendar date -- "2026-09-10", no time
 * and no zone -- while a legacy Apex row carries a full timestamp. Those two
 * cannot be handed to `new Date()` alike: a date-only string is specified to
 * parse as midnight *UTC*, which is the evening before across the Americas, so
 * a transfer made on the 10th would show as the 9th to the investor who made
 * it. A calendar date names a day, not an instant, so it is rebuilt as local
 * midnight and the day survives. A timestamp is genuinely an instant and is
 * left alone to localise as it always has.
 */
export const formatTransferDate = (value: string): string => {
  if (!DATE_ONLY.test(value)) {
    return new Date(value).toLocaleDateString();
  }
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString();
};

const baseRow = (transaction: FirmDataTransaction) => {
  const row: Omit<TransferRow, 'deposit_id' | 'withdrawal_id'> = {
    account_id: transaction.account_id,
    amount: transaction.amount,
    created_at: transaction.trade_date,
  };
  const state = transferState(transaction.status);
  if (state !== undefined) {
    row.state = state;
  }
  return row;
};

export const toTransferList = (transactions: FirmDataTransaction[]): TransferList => {
  const deposits: TransferRow[] = [];
  const withdrawals: TransferRow[] = [];

  transactions.forEach((transaction) => {
    const type = (transaction.transaction_type || '').trim().toLowerCase();
    if (type === DEPOSIT_TYPE) {
      deposits.push({ ...baseRow(transaction), deposit_id: transferId(transaction) });
      return;
    }
    if (type === WITHDRAWAL_TYPE) {
      withdrawals.push({ ...baseRow(transaction), withdrawal_id: transferId(transaction) });
    }
    // Anything else (a trade, a fee) is not a transfer and has no place in a
    // deposits/withdrawals list, so it is dropped rather than guessed at.
  });

  return { deposits, withdrawals };
};

export default toTransferList;
